import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ServerService } from '../../services/ServerService';

export interface BioPagina {
  id: number;
  idEmpresa: number;
  slug: string;
  titulo: string;
  subtitulo: string;
  urlLogo: string;
  corPrimaria: string;
  corSecundaria: string;
  corFundo: string;
  corTexto: string;
  corFundoCard: string;
  corBotaoCta: string;
  borderRadius: number;
  fonte: string;
  textoBadgePromocional: string;
  exibirOfertas: boolean;
  modoExibicaoOfertas: 'slide' | 'lista';
  ativo: boolean;
  links?: BioLink[];
  ofertas?: BioOferta[];
}

export interface BioLink {
  id: number;
  idBioPagina: number;
  _tempId?: string;
  itemType?: 'link' | 'separator';
  modoVisual?: 'icon' | 'image';
  tipo: string;
  titulo: string;
  subtitulo: string;
  icone: string;
  urlIconeCustomizado: string;
  urlImagem?: string;
  url: string;
  etiqueta: string;
  cor: string;
  corSubtitulo: string;
  separatorText?: string;
  separatorShowLine?: boolean;
  ordem: number;
  ativo: boolean;
}

export interface BioOferta {
  id: number;
  idBioPagina: number;
  idProdutoOrigem?: number;
  exibirNaBio: boolean;
  titulo: string;
  descricao: string;
  urlImagem: string;
  precoOriginal: number;
  precoPromocional: number;
  textoBadge: string;
  urlAcao: string;
  ordem: number;
  ativo: boolean;
}

export interface OrigemTrafego {
  origem: string;
  visitas: number;
  visitantesUnicos: number;
  cliques: number;
  ctr: number;
}

export interface CtrPorLink {
  idItem: number;
  titulo: string;
  tipoItem: string;
  posicao: number;
  cliques: number;
  ctr: number;
}

export interface DispositivoMetrica {
  dispositivo: string;
  visitas: number;
  visitantesUnicos: number;
  percentual: number;
}

export interface ResumoBioMetrica {
  totalVisitas: number;
  totalCliques: number;
  taxaConversao: number;
  visitasPorDia: { data: string; quantidade: number }[];
  cliquesPorDia: { data: string; quantidade: number }[];
  cliquesPorItem: { tipoItem: string; idItem: number; titulo: string; quantidade: number; percentual: number }[];
  origens: OrigemTrafego[];
  ctrPorLink: CtrPorLink[];
  dispositivos: DispositivoMetrica[];
}

@Injectable()
export class ServicoBio extends ServerService {

  private endpoint = '/api/bio/admin';

  constructor(protected http: HttpClient) {
    super(http);
  }

  private mapearBioPaginaDaApi(dados: any): BioPagina {
    if (!dados) return null;
    return {
      id: dados.id,
      idEmpresa: dados.idEmpresa,
      slug: dados.slug,
      titulo: dados.title,
      subtitulo: dados.subtitle,
      urlLogo: dados.logoUrl,
      corPrimaria: dados.primaryColor,
      corSecundaria: dados.secondaryColor,
      corFundo: dados.backgroundColor,
      corTexto: dados.textColor || '#333333',
      corFundoCard: dados.cardBackgroundColor || '#FFFFFF',
      corBotaoCta: dados.ctaButtonColor || null,
      borderRadius: dados.borderRadius != null ? dados.borderRadius : 12,
      fonte: dados.font || null,
      textoBadgePromocional: dados.promoBadgeText,
      exibirOfertas: dados.showOffers !== false,
      modoExibicaoOfertas: dados.offersDisplayMode === 'lista' ? 'lista' : 'slide',
      ativo: dados.isActive,
      links: (dados.links || []).map((link: any) => this.mapearBioLinkDaApi(link)),
      ofertas: (dados.offers || []).map((oferta: any) => this.mapearBioOfertaDaApi(oferta))
    };
  }

  private mapearBioPaginaParaApi(bioPagina: Partial<BioPagina>): any {
    const mapa: Record<string, any> = {
      id: bioPagina.id,
      idEmpresa: bioPagina.idEmpresa,
      slug: bioPagina.slug,
      title: bioPagina.titulo,
      subtitle: bioPagina.subtitulo,
      logoUrl: bioPagina.urlLogo,
      primaryColor: bioPagina.corPrimaria,
      secondaryColor: bioPagina.corSecundaria,
      backgroundColor: bioPagina.corFundo,
      textColor: bioPagina.corTexto,
      cardBackgroundColor: bioPagina.corFundoCard,
      ctaButtonColor: bioPagina.corBotaoCta,
      borderRadius: bioPagina.borderRadius,
      font: bioPagina.fonte,
      promoBadgeText: bioPagina.textoBadgePromocional,
      showOffers: bioPagina.exibirOfertas,
      offersDisplayMode: bioPagina.modoExibicaoOfertas,
      isActive: bioPagina.ativo
    };
    // Remove campos undefined para não sobrescrever valores no backend
    Object.keys(mapa).forEach(k => mapa[k] === undefined && delete mapa[k]);
    return mapa;
  }

  private mapearBioLinkDaApi(dados: any): BioLink {
    const urlImagem = dados.imageUrl || dados.customIconUrl || '';
    return {
      id: dados.id,
      idBioPagina: dados.idBioPage,
      itemType: dados.itemType || 'link',
      modoVisual: urlImagem ? 'image' : 'icon',
      tipo: dados.type,
      titulo: dados.title,
      subtitulo: dados.subtitle,
      icone: dados.icon,
      urlIconeCustomizado: urlImagem,
      urlImagem: urlImagem,
      url: dados.url,
      etiqueta: dados.badge || '',
      cor: dados.color || null,
      corSubtitulo: dados.subtitleColor || null,
      separatorText: dados.separatorText || '',
      separatorShowLine: dados.separatorShowLine !== false,
      ordem: dados.sortOrder,
      ativo: dados.isActive
    };
  }

  private mapearBioLinkParaApi(link: Partial<BioLink>): any {
    const urlImagem = link.urlImagem != null ? link.urlImagem : link.urlIconeCustomizado;
    return {
      id: link.id,
      idBioPage: link.idBioPagina,
      itemType: link.itemType || 'link',
      type: link.tipo,
      title: link.titulo,
      subtitle: link.subtitulo,
      icon: link.icone,
      customIconUrl: urlImagem,
      imageUrl: urlImagem,
      url: link.url,
      badge: link.etiqueta,
      color: link.cor,
      subtitleColor: link.corSubtitulo,
      separatorText: link.separatorText,
      separatorShowLine: link.separatorShowLine !== false,
      sortOrder: link.ordem,
      isActive: link.ativo
    };
  }

  private mapearBioOfertaDaApi(dados: any): BioOferta {
    return {
      id: dados.id,
      idBioPagina: dados.idBioPage,
      idProdutoOrigem: dados.sourceProductId,
      exibirNaBio: dados.showInBio !== false,
      titulo: dados.title,
      descricao: dados.description,
      urlImagem: dados.imageUrl,
      precoOriginal: dados.oldPrice,
      precoPromocional: dados.newPrice,
      textoBadge: dados.badgeText,
      urlAcao: dados.actionUrl,
      ordem: dados.sortOrder,
      ativo: dados.isActive
    };
  }

  private mapearBioOfertaParaApi(oferta: Partial<BioOferta>): any {
    return {
      id: oferta.id,
      idBioPage: oferta.idBioPagina,
      sourceProductId: oferta.idProdutoOrigem,
      showInBio: oferta.exibirNaBio,
      title: oferta.titulo,
      description: oferta.descricao,
      imageUrl: oferta.urlImagem,
      oldPrice: oferta.precoOriginal,
      newPrice: oferta.precoPromocional,
      badgeText: oferta.textoBadge,
      actionUrl: oferta.urlAcao,
      sortOrder: oferta.ordem,
      isActive: oferta.ativo
    };
  }

  private mapearResumoMetricaDaApi(dados: any): ResumoBioMetrica {
    return {
      totalVisitas: dados.totalVisitas || 0,
      totalCliques: dados.totalCliques || 0,
      taxaConversao: dados.taxaConversao || 0,
      visitasPorDia: (dados.visitasPorDia || []).map((visita: any) => ({
        data: visita.date,
        quantidade: visita.count
      })),
      cliquesPorDia: (dados.cliquesPorDia || []).map((clique: any) => ({
        data: clique.date,
        quantidade: clique.count
      })),
      cliquesPorItem: (dados.cliquesPorItem || []).map((item: any) => ({
        tipoItem: item.itemType,
        idItem: item.itemId,
        titulo: item.title,
        quantidade: item.count,
        percentual: item.percentual
      })),
      origens: (dados.origens || []).map((o: any) => ({
        origem: o.origem,
        visitas: o.visitas || 0,
        visitantesUnicos: o.visitantesUnicos || 0,
        cliques: o.cliques || 0,
        ctr: o.ctr || 0
      })),
      ctrPorLink: (dados.ctrPorLink || []).map((c: any) => ({
        idItem: c.idItem,
        titulo: c.titulo,
        tipoItem: c.tipoItem,
        posicao: c.posicao,
        cliques: c.cliques || 0,
        ctr: c.ctr || 0
      })),
      dispositivos: (dados.dispositivos || []).map((d: any) => ({
        dispositivo: d.dispositivo || 'Outro',
        visitas: d.visitas || 0,
        visitantesUnicos: d.visitantesUnicos || 0,
        percentual: d.percentual || 0
      }))
    };
  }

  obterBioPagina(idEmpresa: number): Promise<BioPagina> {
    return this.obtenha(`${this.endpoint}/page/${idEmpresa}`, {}).then((dados: any) => this.mapearBioPaginaDaApi(dados));
  }

  salvarBioPagina(bioPagina: BioPagina): Promise<BioPagina> {
    return this.facaPost(`${this.endpoint}/page`, this.mapearBioPaginaParaApi(bioPagina)).then((dados: any) => this.mapearBioPaginaDaApi(dados));
  }

  atualizarBioPagina(id: number, bioPagina: Partial<BioPagina>): Promise<BioPagina> {
    return this.facaPut(`${this.endpoint}/page/${id}`, this.mapearBioPaginaParaApi(bioPagina)).then((dados: any) => this.mapearBioPaginaDaApi(dados));
  }

  alternarBioPagina(id: number, ativo: boolean): Promise<any> {
    return this.facaPut(`${this.endpoint}/page/${id}/toggle`, { isActive: ativo });
  }

  verificarSlug(id: number, slug: string): Promise<any> {
    return this.obtenha(`${this.endpoint}/page/${id}/slug-check/${slug}`, {});
  }

  obterLinks(idBioPagina: number): Promise<BioLink[]> {
    return this.obtenha(`${this.endpoint}/links/${idBioPagina}`, {}).then((dados: any[]) => (dados || []).map((link: any) => this.mapearBioLinkDaApi(link)));
  }

  salvarLink(link: BioLink): Promise<BioLink> {
    return this.facaPost(`${this.endpoint}/links`, this.mapearBioLinkParaApi(link)).then((dados: any) => this.mapearBioLinkDaApi(dados));
  }

  atualizarLink(id: number, link: BioLink): Promise<BioLink> {
    return this.facaPut(`${this.endpoint}/links/${id}`, this.mapearBioLinkParaApi(link)).then((dados: any) => this.mapearBioLinkDaApi(dados));
  }

  excluirLink(id: number): Promise<any> {
    return this.remova(`${this.endpoint}/links/${id}`, {});
  }

  reordenarLinks(idBioPagina: number, ids: number[]): Promise<any> {
    return this.facaPut(`${this.endpoint}/links/reorder`, { idBioPage: idBioPagina, ids });
  }

  obterProdutosPromocionais(): Promise<any[]> {
    return this.obtenha(`${this.endpoint}/promotional-products`, { t: Date.now() });
  }

  obterOfertas(idBioPagina: number): Promise<BioOferta[]> {
    return this.obtenha(`${this.endpoint}/offers/${idBioPagina}`, { t: Date.now() }).then((dados: any[]) => (dados || []).map((oferta: any) => this.mapearBioOfertaDaApi(oferta)));
  }

  sincronizarOfertas(): Promise<BioOferta[]> {
    return this.facaPost(`${this.endpoint}/offers/sync`, {}).then((dados: any[]) => (dados || []).map((oferta: any) => this.mapearBioOfertaDaApi(oferta)));
  }

  salvarOferta(oferta: BioOferta): Promise<BioOferta> {
    return this.facaPost(`${this.endpoint}/offers`, this.mapearBioOfertaParaApi(oferta)).then((dados: any) => this.mapearBioOfertaDaApi(dados));
  }

  atualizarOferta(id: number, oferta: BioOferta): Promise<BioOferta> {
    return this.facaPut(`${this.endpoint}/offers/${id}`, this.mapearBioOfertaParaApi(oferta)).then((dados: any) => this.mapearBioOfertaDaApi(dados));
  }

  excluirOferta(id: number): Promise<any> {
    return this.remova(`${this.endpoint}/offers/${id}`, {});
  }

  reordenarOfertas(idBioPagina: number, ids: number[]): Promise<any> {
    return this.facaPut(`${this.endpoint}/offers/reorder`, { idBioPage: idBioPagina, ids });
  }

  obterMetricas(idBioPagina: number, periodo: string): Promise<ResumoBioMetrica> {
    return this.obtenha(`${this.endpoint}/metrics/${idBioPagina}`, { periodo }).then((dados: any) => this.mapearResumoMetricaDaApi(dados));
  }
}
